import CheckmarkIcon from "assets/CheckmarkIcon";
import axios from "axios";
import FormData from "form-data";
import React, { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import InputContainer from "./InputContainer";

// TODO: Error-handling

const Upload = ({
  label = "Click or drop a file to upload",
  draggingLabel = "Drop the file here",
  accept = [
    "image/*",
    "application/pdf",
    "application/msword",
    "application/rtf",
    "text/plain"
  ],
  disabled = false,
  multiple = false,
  maxFiles = 1,
  minSize = 1,
  maxSize = 10485760, // in bytes, 10MB
  onError = () => {},
  onComplete = () => {},
  showErrors = true,
  overrideComplete = false, // useful if you just want to show confirmation that something is done
  overrideFileName
}) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(overrideComplete);
  const [fileName, setFileName] = useState(overrideFileName || "");
  const [fileKey, setFileKey] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (complete) {
      setLoading(false);
      onComplete({ key: fileKey, fileName });
    }
  }, [complete]);

  const truncateFileName = ({ fileName: fn = fileName, max = 30 } = {}) =>
    `${
      fn.length > max
        ? `${fn.slice(0, Math.floor(max / 2) || 1)}...${fn.slice(
            -(Math.floor(max / 2) || 1)
          )}`
        : fn
    }`;

  const getSignedUrl = async ({ name, type }) => {
    const signedUrlEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/v2/signedUrl/userDispute`;
    const signedUrlPayload = { name, type };
    const signedUrlOptions = {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt")}`
      }
    };
    const response = await axios.post(
      signedUrlEndpoint,
      signedUrlPayload,
      signedUrlOptions
    );
    return response;
  };

  const uploadFile = async ({ file, fields, url }) => {
    const formData = new FormData();
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });
    formData.append("file", file);
    
    try {
      const res = await axios.post(url, formData, {
        onUploadProgress: progressEvent => onProgress(progressEvent)
      });
      setComplete(true);
    } catch (error) {
      onError("Error uploading file");
    }
  };

  const onProgress = data => {
    const { loaded, total } = data;
    const prog = Math.floor((loaded / total) * 100);
    setProgress(prog);
  };

  const onDrop = useCallback(async acceptedFiles => {
    console.log("acceptedFiles:", acceptedFiles);
    setComplete(false);
    setFileName("");
    
    if (!(acceptedFiles || []).length) {
      setError("Unsupported type, or too large (10MB max)");
      return;
    }

    const file = acceptedFiles[0];
    const { name, type } = file;
    
    setFileName(name);
    setError(null);
    setLoading(true);
    
    const reader = new FileReader();
    reader.onabort = () => {
      const message = "File read aborted";
      console.log(message);
      setError(message);
      onError(message);
      setLoading(false);
    };
    reader.onerror = () => {
      const message = "Couldn't read file";
      console.log(message);
      setError(message);
      onError(message);
      setLoading(false);
    };

    reader.onload = async () => {
      const { data } = await getSignedUrl({ name, type });
      const { signedRequest: { url, fields, fields: { key } = {} } = {} } =
        data || {};
      setFileKey(key);
      await uploadFile({ file, fields, url });
      setLoading(false);
    };

    reader.readAsText(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    disabled,
    multiple,
    maxFiles,
    minSize,
    maxSize
  });

  const renderUpload = () => (
    <div
      className="w-full flex items-center justify-center px-4 py-4 outline-none"
      {...getRootProps()}
    >
      <input {...getInputProps()} className="w-full h-full" />
      <i
        className={`fa ${
          isDragActive ? "fa-file-download" : "fa-cloud-upload"
        }`}
      />
      <span className="ml-2 select-none">
        {isDragActive ? draggingLabel : label}
      </span>
    </div>
  );

  const renderLoading = () => (
    <div className="w-full flex items-center justify-center px-4 py-4">
      <i className="fas fa-sync fa-spin" />
      <span className="ml-4 select-none">{truncateFileName()}</span>
    </div>
  );

  const renderComplete = () => (
    <div className="w-full flex items-center justify-center px-4 py-4">
      <CheckmarkIcon />
      <span className="ml-4 select-none">{truncateFileName()}</span>
    </div>
  );

  return (
    <div>
      <InputContainer
        className={`${complete ? "" : "cursor-pointer hover:bg-gray-200"} ${
          isDragActive ? "bg-gray-200" : ""
        }`}
        style={{
          padding: 0,
          ...(loading && {
            background: `linear-gradient(to right, white 0%, #26E3BA ${progress}%, white 0%, white 0%)`
          })
        }}
      >
        {loading
          ? renderLoading()
          : complete
          ? renderComplete()
          : renderUpload()}
      </InputContainer>
      {showErrors && error && <div className="text-red-500 mt-2">{error}</div>}
    </div>
  );
};

export default Upload;
